import { useEffect } from 'react';
import { Actions } from '../components';
import CardLocation from '../components/CardLocation/CardLocation';
import ContactsList from '../components/ContactsList/ContactsList';
import NavBar from '../components/main/NavBar';
import UserProfileImage from '../components/UserProfilePicture/UserProfileImage';
import { IEmployee } from '../interfaces';
import type { TemplateProps, VcardContextData } from './contract';

// Der Kontrakt liefert Bilder als data:-URI (Backend normalisiert, s. contract-v1 toDataUri).
// Die Bestandskomponenten (NavBar/UserProfileImage/CardLocation) prefixen aber selbst mit
// `data:image/png;base64, ` — deshalb hier zurück auf rohes base64, sonst Doppel-Prefix.
function toRawBase64(value?: string): string {
  if (!value) return '';
  const marker = ';base64,';
  const idx = value.startsWith('data:') ? value.indexOf(marker) : -1;
  return idx >= 0 ? value.slice(idx + marker.length) : value;
}

// Sicherer Platzhalter für eine fehlende Team-Adresse (kein Team ODER Team ohne
// address) — verhindert, dass Bestandskomponenten (Actions) auf undefined-Feldern crashen.
const SAFE_ADDRESS_PLACEHOLDER = { team: '', city: '', street: '', houseNumber: 0, zip: '', latitude: '0', longtitude: '0' };

// Adapter Kontrakt-Kontext → bisheriges IEmployee-Format der Bestandskomponenten.
function toEmployee(data: VcardContextData): IEmployee {
  const { vcard, brand } = data;
  return {
    externalId: '',
    urlKey: '',
    base64ProfileImage: toRawBase64(vcard.profileImage),
    role: vcard.role || '',
    email: vcard.email || '',
    firstName: vcard.firstName || '',
    lastName: vcard.lastName || '',
    phone: vcard.phone || '',
    mobile: vcard.mobile || '',
    whatsapp: vcard.whatsapp || '',
    facebook: vcard.facebook || '',
    googleBusinessMessage: vcard.googleBusinessMessage || '',
    website: vcard.website || '',
    linkedin: vcard.linkedin || '',
    brandKey: {
      brandName: brand.name || '',
      // Logo als VOLLE data:-URI durchreichen (korrekte MIME, inkl. SVG) — NavBar
      // rendert `src` direkt. Nur das Profilbild bleibt rohes base64 (vcf-Export).
      base64Logo: brand.logo || '',
      primaryColor: brand.primaryColor || '',
    },
    // IEmployee.teams ist nicht optional (Bestandskomponente `Actions` liest ungeguarded
    // teams.address.*) — ohne Team (oder mit Team, aber ohne address) einen leeren,
    // sicheren Platzhalter liefern statt undefined, damit Actions nicht crasht.
    // CardLocation wird separat über data.vcard.team && data.vcard.team.address gegatet
    // (siehe unten) und zeigt den Platzhalter also nie an.
    // team.address ist im Kontrakt bewusst locker typisiert (Record<string, unknown>),
    // IAddress verlangt konkrete Felder — Cast über `unknown` nötig (tsc), Shape ist
    // strukturell identisch (siehe Task-Brief-Hinweis zu ContextOpeningHour/IOpeningHour).
    teams: vcard.team
      ? ({
          name: vcard.team.name || '',
          // Team-Logo als volle data:-URI (korrekte MIME, inkl. SVG) → CardLocation `src` direkt.
          base64TeamLogo: vcard.team.logo || '',
          address: vcard.team.address ?? SAFE_ADDRESS_PLACEHOLDER,
          openingHours: vcard.team.openingHours || [],
        } as unknown as IEmployee['teams'])
      : ({
          name: '',
          base64TeamLogo: '',
          address: SAFE_ADDRESS_PLACEHOLDER,
          openingHours: [],
        } as IEmployee['teams']),
  } as IEmployee;
}

export function ClassicTemplate({ data }: TemplateProps) {
  const employee = toEmployee(data);
  const primaryColor = data.brand.primaryColor || '#000000';
  const base64Logo = data.brand.logo || '';

  // Bestands-Konvention: Map & Buttons lesen die Brand-Farbe aus sessionStorage.
  sessionStorage.setItem('primaryColor', primaryColor);
  useEffect(() => {
    sessionStorage.setItem('primaryColor', primaryColor);
  }, [primaryColor]);

  return (
    <div style={{ backgroundColor: '#f5f5f5', height: '100%' }}>
        <NavBar logo={base64Logo} />

        <UserProfileImage
          profileImage={employee.base64ProfileImage}
          name={`${employee.firstName} ${employee.lastName}`}
          role={employee.role}
        />

        <Actions employee={employee} />

        <ContactsList
          contacts={{
            email: employee.email,
            phone: employee.phone,
            mobile: employee.mobile,
            whatsapp: employee.whatsapp,
            facebook: employee.facebook,
            linkedin: employee.linkedin,
            googleBusinessMessage: employee.googleBusinessMessage,
            website: employee.website,
          }}
        />

        {data.vcard.team && data.vcard.team.address && <CardLocation {...{ ...employee.teams }} />}

        <div
          style={{
            textAlign: 'center',
            fontSize: '10px',
            color: '#999',
            padding: '8px 0 16px 0',
            marginTop: '8px',
          }}
        >
          Map data © CARTO © OpenStreetMap
        </div>
      </div>
  );
}
